/*
  MasterForce TCG · Search Tactical v9.9
  Motor de intención: título, número, ilustrador, rareza, set y taxonomía confirmada.
  No usa un índice local ficticio ni expone todo el catálogo en HTML.
  Fuente: Shopify Predictive Search (máximo 10) + sección Liquid paginada (muestra limitada).
  Conserva clases y rutas de assets v9.8 para actualización sin archivos huérfanos.
*/
(() => {
  'use strict';

  const SELECTOR = '[data-mf-search]';
  const RARITIES = ['Special Illustration Rare','Illustration Rare','Pikachu Rare',
    'Futuristic Rare','Double Rare','Common','Rare'];
  const ALIASES = {
    'Special Illustration Rare':['special illustration rare','special ill','illustration','illus','sir','sar','ilustracion especial','rara de ilustracion especial'],
    'Illustration Rare':['illustration rare','illustration','illus','ir','rara de ilustracion','ilustracion rara'],
    'Pikachu Rare':['pikachu rare'],
    'Futuristic Rare':['futuristic rare'],
    'Double Rare':['double rare','doble rara'],
    'Common':['common','comun'],
    'Rare':['rare','rara'],
    'Holo':['holo','holografica'],
    'Reverse Holo':['reverse holo','reverse','reversa'],
    'English':['english','ingles'],
    'Japanese':['japanese','japones'],
    'Español':['espanol','spanish'],
    'Trainer':['trainer','entrenador'],
    'Energy':['energy','energia'],
    'Monster':['monster','monstruo'],
    'Basic':['basic','basico'],
    'Stage 1':['stage 1','fase 1'],
    'Stage 2':['stage 2','fase 2'],
    'Singles':['singles','single','cartas sueltas','carta individual'],
    'Spell':['spell','magia'],
    'Leader':['leader','lider'],
    'Lightning':['lightning','rayo'],
    'Psychic':['psychic','psiquico'],
    'Darkness':['darkness','siniestro'],
    'Fire':['fire','fuego'],
    'Water':['water','agua'],
    'Grass':['grass','planta']
  };
  const GAME_NAMES = {
    pokemon:'Pokémon TCG',yugioh:'Yu-Gi-Oh!',
    magic:'Magic: The Gathering',onepiece:'One Piece Card Game'
  };
  const GAME_ALIASES = {
    pokemon:['pokemon tcg','pokemon','jcc pokemon','pokemon trading card game','tcg pokemon'],
    yugioh:['yugioh','yu-gi-oh!','yu gi oh','yu-gi-oh! tcg'],
    magic:['magic','magic the gathering','magic: the gathering','mtg'],
    onepiece:['one piece','one piece tcg','one piece card game']
  };
  const norm = value => String(value == null ? '' : value)
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase()
    .replace(/[^\p{L}\p{N}\/\-\s]/gu,' ').replace(/\s+/g,' ').trim();
  const equal = (a,b) => norm(a)===norm(b);
  const safeTerm = value => String(value||'').replace(/[<>"\\():\r\n]/g,' ')
    .replace(/\b(?:AND|OR|NOT)\b/gi,' ').replace(/\s+/g,' ').trim().slice(0,120);
  const safeTag = value => String(value||'').replace(/["\\\r\n]/g,' ').trim().slice(0,90);
  const words = value => norm(value).split(' ').filter(Boolean);
  const wordMatch = (query,value) => {
    const q=words(query),v=words(value),full=norm(value);
    return q.length>0 && q.every(part=>v.some(word=>word.startsWith(part)) ||
      (part.length>=4 && full.includes(part)));
  };
  const safeUrl = (value,allowExternalImage=false) => {
    try {
      const url=new URL(String(value||''),location.origin);
      if(!['https:','http:'].includes(url.protocol))return '';
      return allowExternalImage||url.origin===location.origin ? url.href : '';
    } catch(_){return '';}
  };
  const imgSource = value => typeof value==='string'?value:
    value&&typeof value==='object'?(value.url||value.src||''):'';
  const canonicalGame = value => {
    for(const [key,labels] of Object.entries(GAME_ALIASES))if(labels.some(label=>equal(value,label)))return key;
    return '';
  };
  function illustratorFromTags(tags){
    if(!Array.isArray(tags))return '';
    for(const tag of tags){
      const match=String(tag||'').trim().match(/^(?:illustrator|ilustrador|art by)\s*:?\s*(.+)$/i);
      if(match && match[1].trim())return match[1].trim();
    }
    return '';
  }
  function taxonomy(query,expansions=[]){
    const q=norm(query);
    if(q.length<1)return [];
    const out=[];
    // Coincidencias por prefijo de palabra, no substring arbitrario en los tags.
    for(const [canonical,aliases] of Object.entries(ALIASES)){
      if(aliases.some(alias=>wordMatch(q,alias)))out.push({value:canonical,
        type:RARITIES.includes(canonical)?'rareza':
          ['Holo','Reverse Holo'].includes(canonical)?'acabado':
          ['English','Japanese','Español'].includes(canonical)?'idioma':'atributo',
        tag:canonical});
    }
    for(const label of expansions){
      if(wordMatch(q,label))out.push({value:label,type:'expansión',tag:label});
    }
    for(const [key,name] of Object.entries(GAME_NAMES)){
      if(GAME_ALIASES[key].some(alias=>wordMatch(q,alias)))out.push({value:name,type:'TCG',tag:null});
    }
    return out.sort((a,b)=>{
      const x=equal(q,a.value)?10:norm(a.value).startsWith(q)?5:0;
      const y=equal(q,b.value)?10:norm(b.value).startsWith(q)?5:0;
      return y-x;
    }).slice(0,8);
  }
  function isRecognizedTerm(query,card,expansions){
    const fields=[card.title,card.number,card.sku,card.illustrator,card.vendor,
      card.expansion,card.rarity,card.cardType,card.attribute,card.finish,card.language,
      card.gameName,...(card.verifiedTags||[])];
    if(fields.some(field=>field&&wordMatch(query,field)))return true;
    const selected=taxonomy(query,expansions);
    if(selected.some(entry=>{
      if(entry.type==='rareza')return equal(card.rarity,entry.value);
      if(entry.type==='expansión')return equal(card.expansion,entry.value);
      if(entry.type==='TCG')return equal(card.gameName,entry.value);
      if(entry.type==='acabado')return equal(card.finish,entry.value);
      if(entry.type==='idioma')return equal(card.language,entry.value);
      return equal(card.cardType,entry.value)||equal(card.attribute,entry.value)||
        (card.verifiedTags||[]).some(tag=>equal(tag,entry.value));
    }))return true;
    // Búsqueda de varias palabras que coinciden en distintos campos relevantes.
    const combined=fields.filter(Boolean).join(' ');
    return wordMatch(query,combined);
  }
  function rank(card,query,expansions){
    const q=norm(query);
    if(!q)return 0;
    const t=norm(card.title),n=norm(card.number);
    if(t===q)return 120;
    if(t.startsWith(q))return 105;
    if(wordMatch(q,card.title))return 90;
    if(n && (n===q||n.startsWith(q)))return 85;
    if(wordMatch(q,card.illustrator))return 80;
    if(wordMatch(q,card.rarity))return 70;
    if(wordMatch(q,card.expansion))return 64;
    if(wordMatch(q,card.cardType)||wordMatch(q,card.attribute))return 60;
    if(wordMatch(q,card.finish)||wordMatch(q,card.language))return 55;
    if(t.includes(q))return 50;
    return taxonomy(query,expansions).length?40:20;
  }

  function init(root){
    if(!root||root.dataset.mfSearchBound==='true')return;
    const $=selector=>root.querySelector(selector);
    const input=$('.mf-header-search__input'),form=$('[data-mf-search-form]');
    const wrap=$('[data-mf-search-wrap]'),overlay=$('.mf-search-overlay');
    const list=$('.mf-header-search__suggestions-list'),count=$('.mf-results-count');
    const empty=$('.mf-search-overlay__empty'),note=$('[data-mf-hybrid-note]');
    const seeAll=$('[data-mf-see-all]'),browseAll=$('[data-mf-browse-all]');
    const advanced=$('[data-mf-advanced]'),toggle=$('[data-mf-filter-toggle]');
    const reset=$('[data-mf-clear-filters]'),rarity=$('[data-mf-rarity]');
    const expansion=$('[data-mf-expansion]'),stock=$('[data-mf-in-stock]');
    const suggestions=$('[data-mf-smart-suggestions]');
    const status=$('[data-mf-status]');
    const tabs=[...root.querySelectorAll('[data-mf-game]')];
    if(![input,form,wrap,overlay,list,count,empty,note,seeAll,browseAll,
      advanced,toggle,reset,rarity,expansion,stock,suggestions,status].every(Boolean))return;
    root.dataset.mfSearchBound='true';
    const state={game:'all',timer:null,controller:null,seq:0,selected:-1,disposed:false};
    const cache=new Map();
    const tagGame={pokemon:root.dataset.tagPokemon||'pokemon tcg',
      yugioh:root.dataset.tagYugioh||'Yu-Gi-Oh!',
      magic:root.dataset.tagMagic||'Magic: The Gathering',
      onepiece:root.dataset.tagOnepiece||'One Piece Card Game'};
    const expansions=String(root.dataset.expansions||'30th Celebration').split(',')
      .map(x=>x.trim()).filter(Boolean);
    for(const text of expansions){const option=document.createElement('option');
      option.value=text;option.textContent=text;expansion.appendChild(option);}
    const sectionId=root.dataset.sectionId||'';
    const feedId=root.dataset.feedId||'mf-search-data-v98';
    const limit=Math.min(10,Math.max(1,Number(root.dataset.limit)||8));
    const minChars=Math.min(5,Math.max(2,Number(root.dataset.minChars)||2));
    const waitMs=Math.min(1000,Math.max(160,Number(root.dataset.debounce)||220));
    const searchRoute=root.dataset.searchUrl||'/search';
    const suggestRoute=root.dataset.predictiveUrl||'/search/suggest';
    const currencyCode=root.dataset.currencyCode||'MXN';
    const term=()=>safeTerm(input.value);
    const filtered=()=>state.game!=='all'||!!(rarity.value||expansion.value||stock.checked);
    const hasStrictFacet=()=>state.game!=='all'||!!(rarity.value||expansion.value);
    const tagQuery=()=>{
      const tags=[];
      if(state.game!=='all')tags.push(tagGame[state.game]);
      if(rarity.value)tags.push(rarity.value);
      if(expansion.value)tags.push(expansion.value);
      return tags.map(tag=>'tag:"'+safeTag(tag)+'"').join(' AND ');
    };
    const titleQuery=()=>term().split(/\s+/).filter(Boolean).map(x=>
      'title:'+x.replace(/[^\p{L}\p{N}\/\-]/gu,'')+'*').filter(x=>x!=='title:*').join(' AND ');
    function searchUrl(q,extra={}){
      const url=new URL(searchRoute,location.origin);
      url.searchParams.set('q',q||'*');url.searchParams.set('type','product');
      url.searchParams.set('options[prefix]','last');
      if(stock.checked)url.searchParams.set('options[unavailable_products]','hide');
      Object.entries(extra).forEach(([key,value])=>url.searchParams.set(key,value));
      return url;
    }
    function finalUrl(){
      // No enviar "charizard AND tag:" al buscador del tema: Shopify puede
      // expandir demasiado la búsqueda y muestra la expresión al visitante.
      return searchUrl(term()||(tagQuery()||'*'));
    }
    function updateLinks(){
      seeAll.href=finalUrl().toString();
      if(term()){
        seeAll.textContent='Ver todos los resultados →';
        browseAll.href=searchUrl(titleQuery()).toString();
        browseAll.textContent='Buscar solo coincidencias en el nombre';
        browseAll.hidden=false;
      } else {
        seeAll.textContent=hasStrictFacet()?'Explorar productos por etiquetas →':'Ver todos los resultados →';
        browseAll.hidden=true;
      }
    }
    function clearResults(){
      list.replaceChildren();empty.hidden=true;state.selected=-1;
      input.removeAttribute('aria-activedescendant');
    }
    function add(parent,element,css,text){
      if(text==null||text==='')return null;
      const node=document.createElement(element);node.className=css;
      node.textContent=String(text);parent.appendChild(node);return node;
    }
    function message(text){note.replaceChildren();note.hidden=!text;
      if(text)add(note,'span','',text);}
    function open(){overlay.hidden=false;input.setAttribute('aria-expanded','true');}
    function close(){overlay.hidden=true;input.setAttribute('aria-expanded','false');
      input.removeAttribute('aria-activedescendant');state.selected=-1;}
    function cancel(){
      state.seq++;clearTimeout(state.timer);state.timer=null;
      if(state.controller){state.controller.abort();state.controller=null;}
      wrap.classList.remove('is-loading');
      root.classList.remove('mf-search-is-updating');
    }
    function drawSuggestions(q){
      suggestions.replaceChildren();
      const values=taxonomy(q,expansions).slice(0,5);
      suggestions.hidden=!values.length;
      if(!values.length)return;
      add(suggestions,'span','mf-search-smart-label','BÚSQUEDAS RELACIONADAS');
      const row=document.createElement('div');row.className='mf-search-smart-row';
      values.forEach(item=>{
        const button=document.createElement('button');button.type='button';
        button.className='mf-search-smart-chip';button.textContent=item.value;
        button.title='Buscar '+item.value;
        button.addEventListener('click',()=>{
          input.value=item.value;input.focus();schedule(true);
        });row.appendChild(button);
      });
      suggestions.appendChild(row);
    }
    function fromLiquid(raw){
      const fields=raw.fields||{},tagList=Array.isArray(raw.tags)?raw.tags:[];
      const hasTag=value=>tagList.some(tag=>equal(tag,value));
      let game=canonicalGame(fields.game);
      if(!game){for(const [key,tag] of Object.entries(tagGame)){
        if(hasTag(tag)||GAME_ALIASES[key].some(hasTag)){game=key;break;}
      }}
      let rare=String(fields.rarity||'').trim();
      if(!rare){
        rare=RARITIES.find(value=>hasTag(value)||
          (ALIASES[value]||[]).some(alias=>hasTag(alias)))||'';
      }
      let set=String(fields.expansion||'').trim();
      if(!set)set=expansions.find(hasTag)||'';
      if(!set&&hasTag('30 aniversario')&&expansions.some(v=>equal(v,'30th Celebration')))set='30th Celebration';
      let number=String(fields.number||'').trim();
      if(!number){const match=String(raw.title||'').match(/\b\d{1,3}[a-z]?\s*\/\s*\d{2,3}\b/i);
        number=match?match[0].replace(/\s+/g,''):'';}
      let finish=String(fields.finish||'').trim();
      // El CSV auditado contiene "reverse holo" en todos los registros;
      // NO aceptar ese tag como prueba del acabado.
      if(!finish&&/\breverse\b/i.test(String(raw.title||'')))finish='Reverse Holo';
      if(!finish&&hasTag('holo'))finish='Holo';
      let language=String(fields.language||'').trim();
      if(!language){for(const value of ['English','Japanese','Español']){
        if(hasTag(value)){language=value;break;}
      }}
      let cardType=String(fields.card_type||'').trim();
      if(!cardType){for(const value of ['Trainer','Energy','Monster','Spell','Leader','Basic','Stage 1','Stage 2']){
        if(hasTag(value)){cardType=value;break;}
      }}
      let attribute=String(fields.attribute||'').trim();
      if(!attribute){for(const value of ['Lightning','Psychic','Darkness','Fire','Water','Grass']){
        if(hasTag(value)){attribute=value;break;}
      }}
      return {
        title:String(raw.title||''),url:safeUrl(raw.url),image:safeUrl(imgSource(raw.image),true),
        vendor:String(raw.vendor||''),price:String(raw.price||''),
        available:typeof raw.available==='boolean'?raw.available:null,
        game,gameName:GAME_NAMES[game]||'',rarity:rare,expansion:set,number,
        sku:String(raw.sku||''),cardType,attribute,finish,language,
        illustrator:illustratorFromTags(tagList),
        verifiedTags:tagList.filter(tag=>/^(?:(?:hp|ps)\s*\d+|\d+\s*(?:hp|ps)|stage [12]|fase [12]|basic|basico|ex|singles pokemon|pokemon singles|cartas pokemon sueltas|cartas sueltas de pokemon|carta individual pokemon)$/i.test(String(tag).trim())),
        source:'liquid'
      };
    }
    function predictivePrice(raw){
      const rawString=String(raw??'').replace(/<[^>]+>/g,'').trim();
      if(!/^\d+(?:\.\d+)?$/.test(rawString))return rawString;
      const value=Number(rawString);
      try{return new Intl.NumberFormat('es-MX',{
        style:'currency',currency:currencyCode,minimumFractionDigits:2
      }).format(value);}catch(_){return '$'+value.toFixed(2);}
    }
    function fromPredictive(raw){
      const image=raw.image||raw.featured_image;
      return {title:String(raw.title||''),url:safeUrl(raw.url),
        image:safeUrl(imgSource(image),true),vendor:String(raw.vendor||''),
        price:predictivePrice(raw.price??raw.price_min??''),
        available:typeof raw.available==='boolean'?raw.available:null,
        rarity:'',expansion:'',number:'',sku:'',game:'',gameName:'',
        cardType:'',attribute:'',finish:'',language:'',illustrator:'',verifiedTags:[],source:'predictive'};
    }
    function matches(card){
      if(term() && !isRecognizedTerm(term(),card,expansions))return false;
      if(state.game!=='all'&&card.game!==state.game)return false;
      if(rarity.value&&!equal(card.rarity,rarity.value))return false;
      if(expansion.value&&!equal(card.expansion,expansion.value))return false;
      if(stock.checked&&card.available!==true)return false;
      return true;
    }
    function productKey(card){
      try{return new URL(card.url,location.origin).pathname.toLowerCase();}
      catch(_){return card.url;}
    }
    function uniqueRanked(cards){
      const result=new Map();
      for(const card of cards){
        if(!card.url||!card.title||!matches(card))continue;
        const id=productKey(card),previous=result.get(id);
        if(!previous || card.source==='liquid')result.set(id,card);
      }
      return [...result.values()].sort((a,b)=>rank(b,term(),expansions)-rank(a,term(),expansions)).slice(0,limit);
    }
    function render(cards,info=''){
      clearResults();updateLinks();drawSuggestions(term());
      const items=uniqueRanked(cards);
      count.textContent='VISTA PREVIA · '+items.length;
      if(!items.length){empty.hidden=false;
        empty.textContent='No encontramos coincidencias verificadas en esta vista previa. Revisa la búsqueda completa.';}
      const fragment=document.createDocumentFragment();
      items.forEach((card,index)=>{
        const li=document.createElement('li');li.id=`mf-option-${sectionId}-${index}`;
        li.setAttribute('role','option');li.setAttribute('aria-selected','false');
        const link=document.createElement('a');link.href=card.url;
        link.className='mf-header-search__suggestion-link';
        const imgBox=document.createElement('span');imgBox.className='mf-header-search__suggestion-image-wrap';
        if(card.image){const img=document.createElement('img');img.src=card.image;
          img.alt=card.title;img.loading='lazy';img.decoding='async';
          img.className='mf-header-search__suggestion-image';imgBox.appendChild(img);
        } else add(imgBox,'span','mf-header-search__suggestion-placeholder',' ');
        const details=document.createElement('span');details.className='mf-header-search__suggestion-content';
        add(details,'span','mf-header-search__suggestion-vendor',card.vendor||'TCG');
        add(details,'span','mf-header-search__suggestion-title',card.title);
        add(details,'span','mf-search-product-meta',
          [card.expansion,card.number,card.rarity].filter(Boolean).join(' · '));
        if(card.illustrator)add(details,'span','mf-search-product-illustrator','Ilustrador: '+card.illustrator);
        add(details,'span','mf-header-search__suggestion-price',card.price);
        if(card.available!==null){const item=add(details,'span','mf-search-product-state',
          card.available?'Disponible':'Agotado');item.dataset.available=String(card.available);}
        link.append(imgBox,details);li.appendChild(link);fragment.appendChild(li);
      });
      list.appendChild(fragment);
      message(info);status.textContent=items.length+' sugerencias verificadas';
      open();
      root.classList.remove('mf-search-is-updating');
    }
    function renderOneLetter(){
      clearResults();drawSuggestions(term());
      const hasSuggestions=!suggestions.hidden;
      if(!hasSuggestions){close();return;}
      count.textContent='SUGERENCIAS';
      empty.hidden=true;
      message('Escribe una letra más para buscar productos en el catálogo.');
      status.textContent='Sugerencias de búsqueda disponibles';
      open();
    }
    async function feed(url,signal){
      const request=new URL(url.toString());request.searchParams.set('section_id',feedId);
      const response=await fetch(request.toString(),{signal,credentials:'same-origin',
        headers:{Accept:'text/html'}});
      if(!response.ok)throw new Error('Sección de búsqueda HTTP '+response.status);
      const doc=new DOMParser().parseFromString(await response.text(),'text/html');
      const payload=doc.querySelector('[data-mf-search-feed]');
      if(!payload)throw new Error('Falta mf-search-data-v98');
      const data=JSON.parse(payload.textContent||'{}');
      if(!data.ok||!Array.isArray(data.products))throw new Error('Respuesta Liquid incompleta');
      return data;
    }
    async function predictive(q,signal){
      const url=new URL(suggestRoute.endsWith('.json')?suggestRoute:
        suggestRoute.replace(/\/$/,'')+'.json',location.origin);
      url.searchParams.set('q',q);
      url.searchParams.set('resources[type]','product');
      url.searchParams.set('resources[limit]','10');
      url.searchParams.set('resources[options][unavailable_products]',stock.checked?'hide':'last');
      const response=await fetch(url.toString(),{signal,credentials:'same-origin',
        headers:{Accept:'application/json'}});
      if(!response.ok)throw new Error('Predictivo HTTP '+response.status);
      const data=await response.json();
      return data?.resources?.results?.products||[];
    }
    function sourceUrls(){
      const q=term(),urls=[];
      if(!q){
        if(tagQuery())urls.push(searchUrl(tagQuery()));
        const filters={};
        if(state.game!=='all')filters['filter.p.m.card.game']=GAME_NAMES[state.game];
        if(rarity.value)filters['filter.p.m.card.rarity']=rarity.value;
        if(expansion.value)filters['filter.p.m.card.expansion']=expansion.value;
        if(Object.keys(filters).length)urls.push(searchUrl('*',filters));
        if(stock.checked&&urls.length===0)urls.push(searchUrl('*'));
      }else{
        // Nunca añadir el TCG al campo q. La validación de filtros ocurre sobre
        // producto (Liquid), y la búsqueda libre sigue siendo relevante.
        const selected=taxonomy(q,expansions);
        const isTaxonomy=selected.some(s=>s.type==='rareza'||s.type==='expansión'||s.type==='acabado'||s.type==='atributo'||s.type==='idioma');
        if(!isTaxonomy)urls.push(searchUrl(titleQuery()));
        urls.push(searchUrl(q));
        for(const tag of selected.filter(s=>s.tag).slice(0,2)){
          urls.push(searchUrl('tag:"'+safeTag(tag.tag)+'"'));
        }
        if(!isTaxonomy && q.length>=3){
          const first=words(q)[0].replace(/[^\p{L}\p{N}\-]/gu,'');
          if(first.length>=3)urls.push(searchUrl('tag:'+first+'*'));
        }
      }
      const visited=new Set();return urls.filter(u=>{
        const k=u.toString();if(visited.has(k))return false;visited.add(k);return true;
      }).slice(0,4);
    }
    async function feedPages(url,signal,budget,maxPages=2){
      const cards=[],seen=new Set();let pages=1;
      for(let page=1;page<=Math.min(maxPages,pages);page++){
        if(budget.left<=0)break;
        budget.left--;
        const u=new URL(url.toString());
        if(page>1)u.searchParams.set('page',String(page));
        const data=await feed(u,signal);
        pages=Math.max(1,Math.min(maxPages,Number(data.pages)||1));
        for(const product of data.products){const card=fromLiquid(product),key=productKey(card);
          if(!seen.has(key)&&matches(card)){seen.add(key);cards.push(card);}}
        if(cards.length>=limit)break;
      }
      return cards;
    }
    async function collect(signal){
      const q=term(),lookups=sourceUrls(),found=[],budget={left:5};
      let failures=0,successful=0;
      // Peticiones acotadas: máximo 1 predictivo + 5 páginas Liquid
      // por consulta; las fuentes adicionales solo se lanzan si faltan datos.
      const firstCalls=lookups.slice(0,2).map(u=>feedPages(u,signal,budget,2));
      if(q.length>=minChars&&!hasStrictFacet()){
        firstCalls.unshift(predictive(q,signal).then(items=>items.map(fromPredictive)));
      }
      const results=await Promise.allSettled(firstCalls);
      for(const result of results){
        if(result.status==='fulfilled'){found.push(...result.value);successful++;}
        else if(result.reason?.name==='AbortError')throw result.reason;
        else failures++;
      }
      let current=uniqueRanked(found);
      if(current.length<limit){
        for(const u of lookups.slice(2)){
          if(current.length>=limit)break;
          if(budget.left<=0)break;
          try{found.push(...await feedPages(u,signal,budget,2));successful++;current=uniqueRanked(found);}
          catch(error){if(error.name==='AbortError')throw error;failures++;}
        }
      }
      if(!successful)throw new Error('No se pudo consultar ninguna fuente de Shopify');
      const incomplete=failures>0?
        'No se pudieron consultar todas las fuentes. Esta vista puede estar incompleta.':
        hasStrictFacet()?'Vista previa filtrada: consulta una muestra, no el catálogo completo.':'';
      return {cards:current,info:incomplete};
    }
    const cacheKey=()=>[norm(term()),state.game,rarity.value,expansion.value,
      Number(stock.checked)].join('|');
    async function run(){
      if(state.disposed)return;
      const seq=state.seq,q=term();
      if(q.length<minChars&&!filtered()){
        if(q.length===1)renderOneLetter();else close();return;
      }
      const key=cacheKey(),hit=cache.get(key);
      if(hit&&Date.now()-hit.at<20000){render(hit.cards,hit.info);return;}
      const controller=typeof AbortController==='function'?new AbortController():null;
      state.controller=controller;
      wrap.classList.add('is-loading');root.classList.add('mf-search-is-updating');
      status.textContent='Buscando productos...';
      try{
        const result=await collect(controller?.signal);
        if(state.disposed||seq!==state.seq)return;
        cache.set(key,{...result,at:Date.now()});
        if(cache.size>35)cache.delete(cache.keys().next().value);
        render(result.cards,result.info);
      }catch(error){
        if(error.name==='AbortError'||seq!==state.seq)return;
        clearResults();drawSuggestions(term());count.textContent='BÚSQUEDA COMPLETA';
        message('La vista previa no está disponible. Puedes abrir los resultados completos de Shopify.');
        status.textContent='La vista previa no está disponible';open();
      }finally{
        if(seq===state.seq){wrap.classList.remove('is-loading');
          root.classList.remove('mf-search-is-updating');state.controller=null;}
      }
    }
    function schedule(immediate=false){
      cancel();updateLinks();drawSuggestions(term());
      const q=term();
      if(q.length<minChars&&!filtered()){
        if(q.length===1)renderOneLetter();else close();return;
      }
      // Mantener los resultados previos durante la petición, y evitar que
      // parezcan corresponder a una consulta nueva.
      if(!overlay.hidden&&q.length>=minChars){root.classList.add('mf-search-is-updating');
        status.textContent='Actualizando sugerencias';}
      state.timer=setTimeout(run,immediate?0:waitMs);
    }
    function setGame(game){
      state.game=game;
      tabs.forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.mfGame===game)));
      rarity.disabled=game!=='pokemon';expansion.disabled=game!=='pokemon';
      if(game!=='pokemon'){rarity.value='';expansion.value='';}
      schedule(true);
    }
    function select(index){
      const links=[...list.querySelectorAll('.mf-header-search__suggestion-link')];
      if(index<0||index>=links.length)return;
      state.selected=index;
      links.forEach((link,i)=>{link.classList.toggle('is-active',i===index);
        link.parentElement.setAttribute('aria-selected',String(i===index));});
      links[index].scrollIntoView({block:'nearest'});
      input.setAttribute('aria-activedescendant',links[index].parentElement.id);
    }
    const onOutside=event=>{if(!root.contains(event.target))close();};
    const onUnload=event=>{
      if(event.detail?.sectionId!==sectionId)return;
      state.disposed=true;cancel();document.removeEventListener('click',onOutside);
      document.removeEventListener('shopify:section:unload',onUnload);
      root.removeAttribute('data-mf-search-bound');
    };
    tabs.forEach(tab=>tab.addEventListener('click',()=>setGame(tab.dataset.mfGame)));
    input.addEventListener('input',()=>schedule());
    input.addEventListener('focus',()=>{
      if(term().length||filtered())schedule(true);
    });
    stock.addEventListener('change',()=>schedule(true));
    rarity.addEventListener('change',()=>schedule(true));
    expansion.addEventListener('change',()=>schedule(true));
    toggle.addEventListener('click',()=>{advanced.hidden=!advanced.hidden;
      toggle.setAttribute('aria-expanded',String(!advanced.hidden));});
    reset.addEventListener('click',()=>{rarity.value='';expansion.value='';
      stock.checked=false;setGame('all');});
    form.addEventListener('submit',event=>{event.preventDefault();
      if(!term()&&!filtered()){input.focus();return;}
      location.assign(finalUrl().toString());
    });
    input.addEventListener('keydown',event=>{
      if(event.key==='Escape'){event.preventDefault();close();return;}
      if(overlay.hidden)return;
      const links=list.querySelectorAll('.mf-header-search__suggestion-link');
      if(!links.length)return;
      if(event.key==='ArrowDown'){event.preventDefault();select((state.selected+1)%links.length);}
      if(event.key==='ArrowUp'){event.preventDefault();select((state.selected-1+links.length)%links.length);}
      if(event.key==='Enter'&&state.selected>=0){event.preventDefault();links[state.selected].click();}
    });
    document.addEventListener('click',onOutside);
    document.addEventListener('shopify:section:unload',onUnload);
    updateLinks();drawSuggestions('');
    tabs.forEach(tab=>tab.setAttribute('aria-pressed',String(tab.dataset.mfGame==='all')));
  }

  function initAll(scope){if(scope.matches?.(SELECTOR))init(scope);
    scope.querySelectorAll(SELECTOR).forEach(init);}
  if(typeof module!=='undefined'&&module.exports){
    module.exports={norm,wordMatch,taxonomy,isRecognizedTerm,rank,illustratorFromTags};
  }
  if(typeof document==='undefined')return;
  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',()=>initAll(document),{once:true});
  }else initAll(document);
  if(!window.__mfSearchTacticalV99Bound){window.__mfSearchTacticalV99Bound=true;
    document.addEventListener('shopify:section:load',event=>initAll(event.target));}
})();
